import Places from 'constants/places'

/**
 * Desktop hotel search calendar event overlays.
 *
 * The shape mirrors the `search_calendar_events` API contract so these rows can
 * be swapped for a fetched payload without touching consumers:
 * `location` is where the event physically happens, `searchPlaceIds` is where we
 * choose to surface it. They are deliberately separate — an event pinned to a
 * lat/lng still needs opt-in destination IDs to render against.
 *
 * `searchPlaceIds` are opt-in LE destination IDs. A city does not inherit a
 * country unless both IDs are listed. Dates are inclusive ISO `YYYY-MM-DD` for a
 * single season — add a new row for the next year.
 *
 * Legend labels and hover tooltips are resolved in `useSearchCalendarEventNames`,
 * which prefers a localised per-`id` message and falls back to `title` /
 * `shortDescription` for rows that do not have one.
 */
export const SEARCH_CALENDAR_EVENT_ICON_NAMES = [
  'line_flower',
  'line_glass',
  'line_star',
  'line_music',
  'line_fire',
  'line_snow_flake',
  'line_sun',
  'line_moon',
  'line_heart',
  'line_gift',
  'line_trees',
  'line_water',
  'line_lightbulb',
  'line_ticket',
  'line_wine_beer',
] as const

export type SearchCalendarEventIconName = typeof SEARCH_CALENDAR_EVENT_ICON_NAMES[number]

/** Mirrors the API's `EventType`. The first entry of `eventTypes` is primary. */
export const SEARCH_CALENDAR_EVENT_TYPES = [
  'FESTIVAL',
  'CARNIVAL',
  'CONCERT',
  'MUSIC_FESTIVAL',
  'LIGHT_FESTIVAL',
  'FOOD_AND_DRINK',
  'MARKET',
  'HOLIDAY',
  'RELIGIOUS',
  'SEASONAL',
  'SPORTS',
  'ARTS',
  'FILM',
  'PRIDE',
  'EXPO',
  'FIREWORKS',
  'PARADE',
  'NIGHTLIFE',
  'OTHER',
  'UNKNOWN',
] as const

export type SearchCalendarEventType = typeof SEARCH_CALENDAR_EVENT_TYPES[number]

/**
 * Where the event physically happens.
 *
 * Exactly one of `placeId` or the `lat`/`lng` pair is populated — the API sends
 * coordinates only for events with no LE place of their own (Buñol, Worthy Farm).
 * This is descriptive only; rendering keys off `searchPlaceIds`.
 */
export interface SearchCalendarEventLocation {
  placeId?: string
  lat?: number
  lng?: number
}

export interface SearchCalendarEvent {
  id: string
  /** Untranslated display name, used when no localised message exists for `id`. */
  title: string
  /** Untranslated tooltip copy, used when no localised message exists for `id`. */
  shortDescription?: string
  startDate: string
  endDate: string
  /** Min 1 entry; the first is the primary type. */
  eventTypes: ReadonlyArray<SearchCalendarEventType>
  icon: SearchCalendarEventIconName
  /** Overlay fill colour (hex or CSS colour). */
  color: string
  /** 1 = highest. Curation signal only; it does not order the overlay. */
  rank?: number
  isManuallySelected: boolean
  location: SearchCalendarEventLocation
  /** Destination IDs this event is surfaced against on search. */
  searchPlaceIds: ReadonlyArray<string>
}

const TOKYO_CITY_ID = 'le_cdd0500dc0ef6682fa6ec6d2e6b577c4'
const TOKYO_VICINITY_ID = 'le_1608659ea7ec15440fa87763b3f5d700'
const KYOTO_VICINITY_ID = 'le_c98e367e46aa4e7c4d6823b021865936'
const KYOTO_CITY_ID = 'le_ac10ff1941c540cd87c107330996f4f6'
const OSAKA_VICINITY_ID = 'le_dd6cfa36ca5f428a6c9824630d3a8a96'
const OSAKA_CITY_ID = 'le_ab452534c5ce28c4fbb0e102d4a4fb2e'
const HOKKAIDO_ID = 'le_4d22dc28a212a9727b1637817a7e9748'
const NISEKO_ID = 'le_7a135f50ded719a23dd2b829ff4eaf88'
const BANGKOK_ID = 'le_ff7aa1f9b4062bfee0f99a0453506f1b'
const BANGKOK_ALT_ID = 'le_9cf81d8026a9018052c429cc4e56739b'
const CHIANG_MAI_ID = 'le_f964e87e2c42208253412904eec37214'
const MUNICH_ID = 'le_3b479d2992711d569ed1edc1c9eda1ad'
const RIO_DE_JANEIRO_ID = 'le_761194a04b4a4dba71f91e5ae90a3956'
const NEW_ORLEANS_ID = 'le_ac00d8d42828f07427af0c7091bb6ad4'
const MUMBAI_ID = 'le_fe332db6e17e429d0b2ecafc8a0e3990'
const JAIPUR_ID = 'le_cdf1035c34ec380218a8cc9a43d438f9'
const NEW_DELHI_ID = 'le_da1726aa3f060ae548a37763b9c61d2d'
const LOS_ANGELES_ID = 'le_079230e29195cac1ce62e737740797a0'
const NEW_YORK_ID = 'le_1ada7faf229425b786d87f7d9d5d43f2'
const LAS_VEGAS_ID = 'le_324d1d5faac07d0c5ae13c2213b84ea9'
const LONDON_ID = 'le_66ff0e7c30438d414ff0a941b759f114'
const EDINBURGH_ID = 'le_f01722cf0682667d18119b2cdef492ad'
const AMSTERDAM_ID = 'le_0a4703b5ee768403d6cf1f04b768def7'
const VIENNA_ID = 'le_e1e1e3fc8c15cfa98d47b605f8a7926a'
const VENICE_ID = 'le_2e378cea7a9a763222e0482db0dc9ee7'
const TROMSO_ID = 'le_0dbb3fb9a5cd1d5f8a9075b5bb8070aa'
const DUBAI_EMIRATE_ID = 'le_43baa6762fa81bb43b39c62553b2970d'

export const SEARCH_CALENDAR_EVENTS: ReadonlyArray<SearchCalendarEvent> = [
  {
    id: 'ces_las_vegas',
    title: 'CES (Consumer Electronics Show)',
    shortDescription: 'The world’s largest consumer tech show, filling Las Vegas each January.',
    startDate: '2027-01-06',
    endDate: '2027-01-09',
    eventTypes: ['EXPO'],
    icon: 'line_lightbulb',
    color: '#AED6F1',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: LAS_VEGAS_ID },
    searchPlaceIds: [LAS_VEGAS_ID],
  },
  {
    id: 'australian_open',
    title: 'Australian Open',
    shortDescription: 'The year’s first tennis Grand Slam, filling Melbourne with fans every January.',
    startDate: '2027-01-11',
    endDate: '2027-01-31',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#A9DFBF',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Melbourne.id },
    searchPlaceIds: [Places.Victoria.id, Places.Melbourne.id],
  },
  {
    id: 'mardi_gras',
    title: 'Mardi Gras (New Orleans)',
    shortDescription: 'New Orleans’ carnival, with parades, masks and colourful beads.',
    startDate: '2027-01-30',
    endDate: '2027-02-09',
    eventTypes: ['CARNIVAL'],
    icon: 'line_music',
    color: '#D2B4DE',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 29.9511, lng: -90.0715 },
    searchPlaceIds: [NEW_ORLEANS_ID],
  },
  {
    id: 'venice_carnival',
    title: 'Venice Carnival (Carnevale di Venezia)',
    shortDescription: 'Venice’s masked carnival winding through the canals until Shrove Tuesday.',
    startDate: '2027-01-30',
    endDate: '2027-02-09',
    eventTypes: ['CARNIVAL'],
    icon: 'line_star',
    color: '#C39BD3',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: VENICE_ID },
    searchPlaceIds: [Places.Italy.id, VENICE_ID],
  },
  {
    id: 'rio_carnival',
    title: 'Rio Carnival',
    shortDescription: 'Rio’s street carnival, with samba parades, music and elaborate costumes.',
    startDate: '2027-02-05',
    endDate: '2027-02-13',
    eventTypes: ['CARNIVAL'],
    icon: 'line_music',
    color: '#F7DC6F',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: RIO_DE_JANEIRO_ID },
    searchPlaceIds: [RIO_DE_JANEIRO_ID],
  },
  {
    id: 'lunar_new_year',
    title: 'Lunar New Year / Spring Festival',
    shortDescription: 'The 15-day Spring Festival, with temple fairs, lanterns and family gatherings.',
    startDate: '2027-02-06',
    endDate: '2027-02-20',
    eventTypes: ['FESTIVAL', 'HOLIDAY'],
    icon: 'line_star',
    color: '#F5B7B1',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.China.id },
    searchPlaceIds: [
      Places.China.id,
      Places.HongKong.id,
      Places.Singapore.id,
      Places.Malaysia.id,
      Places.Vietnam.id,
    ],
  },
  {
    id: 'lunar_new_year_hk',
    title: 'Lunar New Year Night Parade & Fireworks',
    shortDescription: 'Hong Kong’s international night parade, followed by fireworks over Victoria Harbour.',
    startDate: '2027-02-06',
    endDate: '2027-02-07',
    eventTypes: ['FESTIVAL', 'FIREWORKS'],
    icon: 'line_star',
    color: '#F1948A',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.HongKong.id },
    searchPlaceIds: [Places.HongKong.id],
  },
  {
    id: 'super_bowl_la',
    title: 'Super Bowl LXI',
    shortDescription: 'The NFL championship game, played at SoFi Stadium in the Los Angeles area.',
    startDate: '2027-02-14',
    endDate: '2027-02-14',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#85C1E9',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: LOS_ANGELES_ID },
    searchPlaceIds: [LOS_ANGELES_ID],
  },
  {
    id: 'sydney_mardi_gras',
    title: 'Sydney Gay and Lesbian Mardi Gras',
    shortDescription: 'One of the world’s largest Pride celebrations, closing with the Oxford Street parade.',
    startDate: '2027-02-20',
    endDate: '2027-03-07',
    eventTypes: ['PRIDE'],
    icon: 'line_heart',
    color: '#F5B7CE',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Sydney.id },
    searchPlaceIds: [Places.NewSouthWales.id, Places.Sydney.id],
  },
  {
    id: 'f1_australian_grand_prix',
    title: 'Formula 1 Australian Grand Prix',
    shortDescription: 'Formula 1’s season-opening race around Albert Park.',
    startDate: '2027-03-04',
    endDate: '2027-03-07',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#F8C471',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Melbourne.id },
    searchPlaceIds: [Places.Victoria.id, Places.Melbourne.id],
  },
  {
    id: 'cherry_blossom',
    title: 'Cherry Blossom Season (Hanami)',
    shortDescription: 'Japan’s sakura season, when parks and temples fill with pale pink blooms. Bloom dates shift with the weather each year.',
    startDate: '2027-03-20',
    endDate: '2027-04-10',
    eventTypes: ['SEASONAL'],
    icon: 'line_flower',
    color: '#F5C6D0',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.Japan.id },
    searchPlaceIds: [
      Places.Japan.id,
      TOKYO_CITY_ID,
      TOKYO_VICINITY_ID,
      KYOTO_VICINITY_ID,
      KYOTO_CITY_ID,
      OSAKA_VICINITY_ID,
      OSAKA_CITY_ID,
      HOKKAIDO_ID,
    ],
  },
  {
    id: 'holi',
    title: 'Holi (Festival of Colours)',
    shortDescription: 'India’s festival of colour, where people throw powdered dyes to welcome spring.',
    startDate: '2027-03-22',
    endDate: '2027-03-23',
    eventTypes: ['FESTIVAL', 'RELIGIOUS'],
    icon: 'line_flower',
    color: '#F5B7CE',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 27.4924, lng: 77.6737 },
    searchPlaceIds: [Places.India.id, Places.Rajasthan.id, MUMBAI_ID, JAIPUR_ID, NEW_DELHI_ID],
  },
  {
    id: 'coachella',
    title: 'Coachella Valley Music and Arts Festival',
    shortDescription: 'Two consecutive three-day weekends of music and art in the California desert.',
    startDate: '2027-04-09',
    endDate: '2027-04-18',
    eventTypes: ['MUSIC_FESTIVAL'],
    icon: 'line_music',
    color: '#F9E79F',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 33.7206, lng: -116.2156 },
    searchPlaceIds: [LOS_ANGELES_ID],
  },
  {
    id: 'songkran',
    title: 'Songkran (Thai New Year Water Festival)',
    shortDescription: 'Thailand’s New Year water festival, with street splashing and temple visits.',
    startDate: '2027-04-13',
    endDate: '2027-04-15',
    eventTypes: ['FESTIVAL'],
    icon: 'line_water',
    color: '#A3E4D7',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.Thailand.id },
    searchPlaceIds: [
      Places.Thailand.id,
      Places.Phuket.id,
      BANGKOK_ID,
      BANGKOK_ALT_ID,
      CHIANG_MAI_ID,
    ],
  },
  {
    id: 'kings_day_amsterdam',
    title: 'King’s Day (Koningsdag)',
    shortDescription: 'The Netherlands’ biggest street party, turning Amsterdam orange with canal parties and free markets.',
    startDate: '2027-04-27',
    endDate: '2027-04-27',
    eventTypes: ['HOLIDAY', 'FESTIVAL'],
    icon: 'line_star',
    color: '#F5B041',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: AMSTERDAM_ID },
    searchPlaceIds: [Places.Netherlands.id, AMSTERDAM_ID],
  },
  {
    id: 'cannes_film_festival',
    title: 'Cannes Film Festival',
    shortDescription: 'The French Riviera’s red-carpet film festival, and one of the world’s most prestigious.',
    startDate: '2027-05-11',
    endDate: '2027-05-22',
    eventTypes: ['FILM'],
    icon: 'line_ticket',
    color: '#D7BDE2',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 43.5528, lng: 7.0174 },
    searchPlaceIds: [Places.France.id],
  },
  {
    id: 'midnight_sun_norway',
    title: 'Midnight Sun',
    shortDescription: '24-hour daylight above the Arctic Circle in northern Norway.',
    startDate: '2027-05-20',
    endDate: '2027-07-22',
    eventTypes: ['SEASONAL'],
    icon: 'line_sun',
    color: '#FAD7A0',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: TROMSO_ID },
    searchPlaceIds: [TROMSO_ID],
  },
  {
    id: 'vivid_sydney',
    title: 'Vivid Sydney',
    shortDescription: 'Australia’s largest festival of light, music and ideas, across 23 nights.',
    startDate: '2027-05-28',
    endDate: '2027-06-19',
    eventTypes: ['LIGHT_FESTIVAL'],
    icon: 'line_lightbulb',
    color: '#A9CCE3',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Sydney.id },
    searchPlaceIds: [Places.NewSouthWales.id, Places.Sydney.id],
  },
  {
    id: 'queenstown_ski_season',
    title: 'Queenstown Ski Season',
    shortDescription: 'New Zealand’s premier ski and adventure-sports season in the Southern Alps.',
    startDate: '2027-06-01',
    endDate: '2027-10-10',
    eventTypes: ['SEASONAL'],
    icon: 'line_snow_flake',
    color: '#D6EAF8',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Queenstown.id },
    searchPlaceIds: [Places.Queenstown.id],
  },
  {
    id: 'ashes_series_england',
    title: 'The Ashes (England vs Australia)',
    shortDescription: 'Cricket’s oldest and most famous rivalry, played across England over six weeks.',
    startDate: '2027-06-18',
    endDate: '2027-08-02',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#ABEBC6',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.UnitedKingdom.id },
    searchPlaceIds: [Places.UnitedKingdom.id, LONDON_ID],
  },
  {
    id: 'glastonbury_festival',
    title: 'Glastonbury Festival',
    shortDescription: 'The world’s most famous multi-act music and arts festival, held at Worthy Farm.',
    startDate: '2027-06-23',
    endDate: '2027-06-27',
    eventTypes: ['MUSIC_FESTIVAL'],
    icon: 'line_music',
    color: '#F5CBA7',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 51.1485, lng: -2.7141 },
    searchPlaceIds: [Places.UnitedKingdom.id, LONDON_ID],
  },
  {
    id: 'wimbledon',
    title: 'Wimbledon Championships',
    shortDescription: 'Tennis’ oldest and most prestigious Grand Slam, over two weeks in London.',
    startDate: '2027-06-28',
    endDate: '2027-07-11',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#A2D9CE',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: LONDON_ID },
    searchPlaceIds: [Places.UnitedKingdom.id, LONDON_ID],
  },
  {
    id: 'san_fermin',
    title: 'San Fermín (Running of the Bulls)',
    shortDescription: 'Pamplona’s festival, best known for the running of the bulls.',
    startDate: '2027-07-06',
    endDate: '2027-07-14',
    eventTypes: ['FESTIVAL', 'SPORTS'],
    icon: 'line_music',
    color: '#F1948A',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 42.8125, lng: -1.6458 },
    searchPlaceIds: [Places.Spain.id],
  },
  {
    id: 'indonesia_independence_day',
    // Nationwide, so only the country ID is listed. A Bali or Ubud search must not
    // inherit it — see the hierarchy test in `searchCalendarEvents.test.ts`.
    title: 'Indonesian Independence Day',
    shortDescription: 'Indonesia’s national day, marked by flag ceremonies, parades and village games.',
    startDate: '2027-08-17',
    endDate: '2027-08-17',
    eventTypes: ['HOLIDAY'],
    icon: 'line_star',
    color: '#F5B7B1',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Indonesia.id },
    searchPlaceIds: [Places.Indonesia.id],
  },
  {
    id: 'edinburgh_fringe',
    title: 'Edinburgh Festival Fringe',
    shortDescription: 'The world’s largest performing arts festival, across Edinburgh each August.',
    startDate: '2027-08-06',
    endDate: '2027-08-30',
    eventTypes: ['ARTS'],
    icon: 'line_ticket',
    color: '#D2B4DE',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: EDINBURGH_ID },
    searchPlaceIds: [Places.UnitedKingdom.id, EDINBURGH_ID],
  },
  {
    id: 'la_tomatina',
    title: 'La Tomatina',
    shortDescription: 'The world’s largest food fight, held over a single morning in Buñol.',
    startDate: '2027-08-25',
    endDate: '2027-08-25',
    eventTypes: ['FESTIVAL'],
    icon: 'line_water',
    color: '#F1948A',
    rank: 2,
    isManuallySelected: true,
    location: { lat: 39.4194, lng: -0.7936 },
    searchPlaceIds: [Places.Spain.id],
  },
  {
    id: 'notting_hill_carnival',
    title: 'Notting Hill Carnival',
    shortDescription: 'Europe’s biggest street carnival, filling West London over the August bank holiday.',
    startDate: '2027-08-28',
    endDate: '2027-08-30',
    eventTypes: ['CARNIVAL'],
    icon: 'line_music',
    color: '#F7DC6F',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: LONDON_ID },
    searchPlaceIds: [Places.UnitedKingdom.id, LONDON_ID],
  },
  {
    id: 'northern_lights_iceland',
    title: 'Northern Lights (Aurora Borealis)',
    shortDescription: 'Peak aurora-viewing season across Iceland’s long, dark winter nights.',
    startDate: '2027-09-01',
    endDate: '2028-03-15',
    eventTypes: ['SEASONAL'],
    icon: 'line_moon',
    color: '#A9DFBF',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.Iceland.id },
    searchPlaceIds: [Places.Iceland.id],
  },
  {
    id: 'oktoberfest',
    title: 'Oktoberfest',
    shortDescription: 'Munich’s beer festival, with tents, music and traditional Bavarian dress.',
    startDate: '2027-09-18',
    endDate: '2027-10-03',
    eventTypes: ['FOOD_AND_DRINK'],
    icon: 'line_glass',
    color: '#F5D0A9',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: MUNICH_ID },
    searchPlaceIds: [Places.Germany.id, MUNICH_ID],
  },
  {
    id: 'rugby_world_cup_australia',
    title: 'Rugby World Cup 2027',
    shortDescription: 'Rugby’s biggest tournament, hosted across seven Australian cities with the final in Sydney.',
    startDate: '2027-10-01',
    endDate: '2027-11-13',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#A3E4D7',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.Australia.id },
    searchPlaceIds: [
      Places.Australia.id,
      Places.NewSouthWales.id,
      Places.Sydney.id,
      Places.Victoria.id,
      Places.Melbourne.id,
      Places.Queensland.id,
      Places.Brisbane.id,
      Places.Perth.id,
      Places.Adelaide.id,
    ],
  },
  {
    id: 'cricket_world_cup',
    title: 'ICC Men’s Cricket World Cup',
    shortDescription: 'Cricket’s biggest global tournament, hosted across southern Africa.',
    startDate: '2027-10-04',
    endDate: '2027-11-21',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#AED6F1',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.SouthAfrica.id },
    searchPlaceIds: [Places.SouthAfrica.id],
  },
  {
    id: 'diwali',
    title: 'Diwali (Festival of Lights)',
    shortDescription: 'India’s festival of lights, marked by lamps, sweets and fireworks.',
    startDate: '2027-10-27',
    endDate: '2027-10-31',
    eventTypes: ['RELIGIOUS', 'HOLIDAY'],
    icon: 'line_star',
    color: '#F6E27A',
    rank: 1,
    isManuallySelected: true,
    location: { placeId: Places.India.id },
    searchPlaceIds: [Places.India.id, Places.Rajasthan.id, MUMBAI_ID, JAIPUR_ID, NEW_DELHI_ID],
  },
  {
    id: 'melbourne_cup_carnival',
    title: 'Melbourne Cup Carnival (Spring Racing Carnival)',
    shortDescription: 'Australia’s biggest racing and social event, across Derby, Cup, Oaks and Stakes days.',
    startDate: '2027-10-30',
    endDate: '2027-11-06',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#F9E79F',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Melbourne.id },
    searchPlaceIds: [Places.Victoria.id, Places.Melbourne.id],
  },
  {
    id: 'japan_autumn_foliage',
    title: 'Japan Autumn Foliage (Koyo)',
    shortDescription: 'Peak autumn leaf season across Japan, led by Kyoto’s temples and gardens.',
    startDate: '2027-11-01',
    endDate: '2027-11-30',
    eventTypes: ['SEASONAL'],
    icon: 'line_trees',
    color: '#F5B041',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: KYOTO_VICINITY_ID },
    searchPlaceIds: [
      Places.Japan.id,
      KYOTO_VICINITY_ID,
      KYOTO_CITY_ID,
      TOKYO_CITY_ID,
      TOKYO_VICINITY_ID,
      OSAKA_VICINITY_ID,
      OSAKA_CITY_ID,
    ],
  },
  {
    id: 'vienna_christmas_market',
    title: 'Vienna Christmas Markets',
    shortDescription: 'Historic Christmas markets across Vienna, led by the Rathausplatz market.',
    startDate: '2027-11-13',
    endDate: '2027-12-26',
    eventTypes: ['MARKET'],
    icon: 'line_gift',
    color: '#D5F5E3',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: VIENNA_ID },
    searchPlaceIds: [VIENNA_ID],
  },
  {
    id: 'niseko_ski_season',
    title: 'Niseko Powder Snow Season',
    shortDescription: 'Japan’s most famous powder-snow ski region, deep in the Hokkaido winter.',
    startDate: '2027-12-01',
    endDate: '2028-02-28',
    eventTypes: ['SEASONAL'],
    icon: 'line_snow_flake',
    color: '#EAF2F8',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: NISEKO_ID },
    searchPlaceIds: [HOKKAIDO_ID, NISEKO_ID],
  },
  {
    id: 'dubai_shopping_festival',
    title: 'Dubai Shopping Festival',
    shortDescription: 'A month of retail and entertainment, with fireworks, concerts and citywide discounts.',
    startDate: '2027-12-15',
    endDate: '2028-01-15',
    eventTypes: ['FESTIVAL'],
    icon: 'line_gift',
    color: '#FAD7A0',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: DUBAI_EMIRATE_ID },
    searchPlaceIds: [Places.UnitedArabEmirates.id, Places.Dubai.id, DUBAI_EMIRATE_ID],
  },
  {
    id: 'sydney_nye',
    title: 'Sydney New Year’s Eve Fireworks',
    shortDescription: 'Sydney Harbour fireworks, one of the world’s most watched New Year’s Eve nights.',
    startDate: '2027-12-31',
    endDate: '2027-12-31',
    eventTypes: ['FIREWORKS'],
    icon: 'line_star',
    color: '#C5D4F5',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: Places.Sydney.id },
    searchPlaceIds: [Places.Australia.id, Places.NewSouthWales.id, Places.Sydney.id],
  },
  {
    id: 'times_square_nye',
    title: 'Times Square New Year’s Eve Ball Drop',
    shortDescription: 'The world’s most famous New Year’s countdown, in the middle of Manhattan.',
    startDate: '2027-12-31',
    endDate: '2027-12-31',
    eventTypes: ['FIREWORKS'],
    icon: 'line_star',
    color: '#AEB6BF',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: NEW_YORK_ID },
    searchPlaceIds: [NEW_YORK_ID],
  },
  {
    id: 'la_2028_olympics',
    title: 'LA 2028 Summer Olympics',
    shortDescription: 'The Summer Olympic Games, hosted across Los Angeles.',
    startDate: '2028-07-14',
    endDate: '2028-07-30',
    eventTypes: ['SPORTS'],
    icon: 'line_ticket',
    color: '#85C1E9',
    rank: 2,
    isManuallySelected: true,
    location: { placeId: LOS_ANGELES_ID },
    searchPlaceIds: [Places.UnitedStates.id, LOS_ANGELES_ID],
  },
]
